/**
 * Attendance punch times are stored as Egypt wall-clock components in UTC
 * fields (see backend attendance-wall-clock.util.ts). Display with timeZone UTC.
 */
export const ATTENDANCE_WALL_CLOCK_TZ = "Africa/Cairo";

export function utcCalendarDateKey(instant = new Date()) {
  return instant.toISOString().slice(0, 10);
}

/** YYYY-MM-DD of the current calendar day in Egypt. */
export function cairoCalendarDateKey(instant = new Date()) {
  return nowAsWallClockUtc(instant).toISOString().slice(0, 10);
}

/**
 * Open app punch whose calendar day is before today.
 * The attendance screen must not treat that punch as today's check-in.
 */
export function isOpenPreviousAttendanceDay(record, todayKey) {
  if (!record?.checkInTime || record?.checkOutTime || !todayKey) return false;
  const day = attendanceRecordDateKey(record.date);
  return Boolean(day) && day < todayKey;
}

/** Normalize attendance `date` (Date-only / ISO) to YYYY-MM-DD. */
export function attendanceRecordDateKey(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

/** Match backend wall-clock UTC convention for live elapsed-time math. */
export function nowAsWallClockUtc(instant = new Date()) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: ATTENDANCE_WALL_CLOCK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const map = Object.fromEntries(
    fmt
      .formatToParts(instant)
      .filter((p) => p.type !== "literal")
      .map((p) => [p.type, p.value]),
  );
  return new Date(
    Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour),
      Number(map.minute),
      Number(map.second),
    ),
  );
}

/** Display check-in / check-out time (HH:mm, 24h). */
export function formatAttendanceTime(value) {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleTimeString("ar-EG", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Build a punch timestamp from the date + HH:mm HR typed.
 * Uses UTC components so it matches stored wall-clock (getUTCHours display)
 * and does not shift with the browser timezone.
 */
export function combineAttendanceWallClock(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const timeMatch = String(timeStr).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!dateMatch || !timeMatch) return null;
  const parsed = new Date(
    Date.UTC(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3]),
      Number(timeMatch[1]),
      Number(timeMatch[2]),
      Number(timeMatch[3] || 0),
    ),
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toWallClockDate(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

/** Split punch timestamp into time + date for compact stacked table cells. */
export function formatAttendanceDateTimeParts(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
    const time = value.length >= 8 ? value.slice(0, 8) : value.slice(0, 5);
    return { time, date: "" };
  }
  const parsed = toWallClockDate(value);
  if (!parsed) return null;
  return {
    time: `${pad2(parsed.getUTCHours())}:${pad2(parsed.getUTCMinutes())}:${pad2(parsed.getUTCSeconds())}`,
    date: `${parsed.getUTCFullYear()}-${pad2(parsed.getUTCMonth() + 1)}-${pad2(parsed.getUTCDate())}`,
  };
}

/** Two-line punch timestamp: HH:mm:ss then YYYY-MM-DD. */
export function formatAttendanceDateTimeCompact(value) {
  const parts = formatAttendanceDateTimeParts(value);
  if (!parts) return "—";
  return parts.date ? `${parts.time}\n${parts.date}` : parts.time;
}

/** Display punch timestamp with date + time (attendance wall-clock). */
export function formatAttendanceDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("ar-EG", {
    timeZone: "UTC",
    hour12: false,
  });
}
